import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import ApEditorMode from '../../../enums/ApEditorMode';
import useAsyncModifyApPlanAction from '../../../hooks/useAsyncModifyApPlanAction';
import useAsyncModifyApTemplateAction from '../../../hooks/useAsyncModifyApTemplateAction';
import { ApActionType } from '../../../sic-lib/enums/ApActionType';
import { RequestStatus } from '../../../sic-lib/enums/RequestStatus';
import { ApEditorActions } from '../../../sic-lib/reducers/actionPlanEditorReducer';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import { ApActionBase } from '../../../sic-lib/types/ActionPlan';
import {
    ApPlanAction,
    ApPlanActionData,
    buildUpdateApPlanAction,
} from '../../../sic-lib/types/ApPlanAction';
import {
    ApTemplateAction,
    buildUpdateApTemplateAction,
} from '../../../sic-lib/types/ApTemplateAction';
import { sanitizeAllowedTags } from '../../../sic-lib/utils/sanitizeHtml';
import { Subtract } from '../../../sic-lib/utils/typeUtils';
import ComposeEmailFields from '../../../types/ComposeEmailFields';
import { draftFromHtml } from '../../../utils/messagingHelpers';
import ComposeEmailRaw from '../../Contact/Communicator/ComposeEmail/ComposeEmailRaw/ComposeEmailRaw';
import ButtonLoading from '../../Shared/ButtonLoading';
import TextFieldFast from '../../Shared/TextFieldFast/TextFieldFast';

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        height: '100%',
    },
    headerWrapper: {
        margin: theme.spacing(2, 0, 1),
        [theme.breakpoints.up(0)]: {
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(1, 2),
        },
    },
    titleWrapper: {
        [theme.breakpoints.up(0)]: {
            margin: theme.spacing(2, 0),
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
            margin: theme.spacing(1, 0),
            padding: theme.spacing(1, 2),
        },
    },
    bodyWrapper: {
        [theme.breakpoints.up(0)]: {
            margin: theme.spacing(2, 0),
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
            margin: theme.spacing(1, 0),
            padding: theme.spacing(2),
        },
    },
    buttonWrapper: {
        [theme.breakpoints.up(0)]: {
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(1, 2),
        },
    },
}));

interface ComponentState extends ComposeEmailFields {
    body: string;
    title: string;
}

type WithOnSaveHandler = {
    showLoading: boolean;
    onSaveHandler: (title: string, subject: string, body: string) => void;
};

type Props = WithOnSaveHandler & {
    rootId: number;
    action: ApActionBase;
    actionIndex: number;
    apEditMode: ApEditorMode;
    apDispatch: React.Dispatch<ApEditorActions>;
};

function ApEditorActionModify({
    rootId,
    action,
    apEditMode,
    actionIndex,
    apDispatch,
    showLoading,
    onSaveHandler,
}: Props) {
    const { classes } = useStyles();

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            editor: EditorState.createEmpty(),
            subject: '',
            body: '',
            title: '',
        },
    );

    React.useEffect(() => {
        let nextState: ApPlanActionData = {
            subject: '',
            body: '',
        };

        switch (apEditMode) {
            case ApEditorMode.template:
                nextState = {
                    ...nextState,
                    ...{
                        subject: (action as ApTemplateAction).subject,
                        body: (action as ApTemplateAction).body,
                    },
                };
                break;
            case ApEditorMode.plan:
                nextState = {
                    ...nextState,
                    ...{
                        subject: (action as ApPlanAction).data.subject,
                        body: (action as ApPlanAction).data.body,
                    },
                };
                break;
        }

        if (action.type === ApActionType.email) {
            nextState = {
                ...nextState,
                ...{ editor: draftFromHtml(nextState.body || '') },
            };
        }

        compDispatch(nextState);
    }, [action.actionId]);

    const handleCancel = () => {
        apDispatch({
            type: 'SET_EDITOR_EDIT',
            actionIndex: -1,
            setEdit: false,
        });
    };

    const handleSave = () => {
        let body =
            action.type === ApActionType.email
                ? sanitizeAllowedTags(stateToHTML(compState.editor.getCurrentContent()))
                : compState.body;

        onSaveHandler(compState.title, compState.subject, body);
    };

    return (
        <div className={classes.root}>
            <div className={classes.headerWrapper}>
                <Typography variant="h6" component="h3">
                    {`Edit ${action.type} Action`}
                </Typography>
            </div>
            <div className={classes.titleWrapper}>
                <TextFieldFast
                    fieldName="title"
                    fieldValue={compState.title}
                    label="Action Title"
                    variant="outlined"
                    onChangeHandler={compDispatch}
                />
            </div>
            <div className={classes.bodyWrapper}>
                {action.type === ApActionType.email && (
                    <ComposeEmailRaw
                        subjectValue={compState.subject}
                        editorState={compState.editor}
                        onUpdateHandler={compDispatch}
                        isDisabled={false}
                    />
                )}
                {action.type !== ApActionType.email && (
                    <TextFieldFast
                        fieldName="body"
                        fieldValue={compState.body}
                        label="Message Body"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        maxRows={4}
                        onChangeHandler={compDispatch}
                    />
                )}
            </div>
            <div className={classes.buttonWrapper}>
                <Grid
                    container
                    direction="row"
                    wrap="nowrap"
                    alignItems="center"
                    justifyContent="flex-end"
                    spacing={2}
                >
                    <Grid item xs="auto">
                        <Button onClick={handleCancel}>Cancel</Button>
                    </Grid>
                    <Grid item xs="auto">
                        <ButtonLoading
                            isLoading={showLoading}
                            onClick={handleSave}
                            variant="outlined"
                            color="secondary"
                        >
                            Save
                        </ButtonLoading>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
}

/**
 * withSaveTemplateAction
 *
 * @description
 * A Higher-Order-Component which injects the implementation for modifying an existing action plan
 * template action into it's wrapped component
 */
function withSaveTemplateAction(Component: (props: Props) => React.ReactElement) {
    return function WithSaveTemplateAction(props: Subtract<Props, WithOnSaveHandler>) {
        const { requestStatus, asyncModifyApTemplateAction } = useAsyncModifyApTemplateAction();

        const handleSave = async (title: string, subject: string, body: string) => {
            try {
                const updateObj = buildUpdateApTemplateAction({
                    ...(props.action as ApTemplateAction),
                    ...{
                        title,
                        subject,
                        body,
                    },
                });

                const asyncResult = await asyncModifyApTemplateAction(
                    props.rootId,
                    props.action.actionId,
                    updateObj,
                );

                if (asyncResult) {
                    props.apDispatch({
                        type: 'MODIFY_EDITOR_ACTION',
                        action: {
                            ...updateObj,
                            ...{
                                actionId: props.action.actionId,
                                actions: props.action.actions,
                            },
                        },
                        actionIndex: props.actionIndex,
                    });
                }
            } catch (error: any) {
                if (error.isFormError) {
                    // TODO: pass form error to component
                }
            }
        };

        return (
            <Component
                {...props}
                showLoading={requestStatus === RequestStatus.active}
                onSaveHandler={handleSave}
            />
        );
    };
}

/**
 * withSavePlanAction
 *
 * @description
 * A Higher-Order-Component which injects the implementation for modifying an existing action plan
 * template action into it's wrapped component
 */
function withSavePlanAction(Component: (props: Props) => React.ReactElement) {
    return function WithSavePlanAction(props: Subtract<Props, WithOnSaveHandler>) {
        const { requestStatus, asyncModifyApPlanAction } = useAsyncModifyApPlanAction();

        const handleSave = async (title: string, subject: string, body: string) => {
            const updateObj = buildUpdateApPlanAction({
                actionId: props.action.actionId,
                type: props.action.type,
                assignedUser: (props.action as ApPlanAction).assignedUser,
                title: title,
                body: body,
                subject: subject,
                mediaUrls: (props.action as ApPlanAction).data.mediaUrls,
            });

            const asyncResult = await asyncModifyApPlanAction(
                props.rootId,
                props.action.actionId,
                updateObj,
            );

            if (asyncResult) {
                props.apDispatch({
                    type: 'MODIFY_EDITOR_ACTION',
                    action: { ...props.action, ...updateObj },
                    actionIndex: props.actionIndex,
                });
            }
        };

        return (
            <Component
                {...props}
                showLoading={requestStatus === RequestStatus.active}
                onSaveHandler={handleSave}
            />
        );
    };
}

/**
 * withEitherApAction
 *
 * @descrption
 * A Heigher-Order-Component which renders either the component injected with Ap Plan action or
 * Ap template logic depending on the apEditMode prop
 */
function withEitherApAction<P extends Subtract<Props, WithOnSaveHandler>>(
    ApTemplateCompoent: (props: P) => React.ReactElement,
    ApPlanComponent: (props: P) => React.ReactElement,
) {
    return function WithEitherApActionProps(props: P) {
        return props.apEditMode === ApEditorMode.template ? (
            <ApTemplateCompoent {...props} />
        ) : (
            <ApPlanComponent {...props} />
        );
    };
}

/**
 * ModifyApTemplateAction
 *
 * @description
 * Composed ApEditorCreateAction component containing on-save handler logic for saving a new
 * Ap Template action
 */
const ModifyApTemplateAction = withSaveTemplateAction(ApEditorActionModify);

/**
 * ModifApPlanAction
 *
 * @description
 * Composed ApEditorCreateAction component containing on-save handler logic for saving a new
 * Ap Plan action
 */
const ModifApPlanAction = withSavePlanAction(ApEditorActionModify);

export default withEitherApAction(ModifyApTemplateAction, ModifApPlanAction);
